import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Toast, type Variant } from '../components/ds/Toast';
import styles from './toast.module.css';

interface ToastEntry {
  id: number;
  variant: Variant;
  title: string;
  message?: string;
}

interface ToastContextValue {
  toast: (variant: Variant, title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 5000;

function ToastItem({ entry, onDismiss }: { entry: ToastEntry; onDismiss: (id: number) => void }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const start = useCallback(() => {
    timerRef.current = setTimeout(() => onDismiss(entry.id), AUTO_DISMISS_MS);
  }, [entry.id, onDismiss]);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    start();
    return clear;
  }, [start, clear]);

  return (
    <div
      className={styles.item}
      onMouseEnter={clear}
      onMouseLeave={start}
    >
      <Toast
        variant={entry.variant}
        title={entry.title}
        message={entry.message}
        accentBar
        onClose={() => onDismiss(entry.id)}
      />
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((variant: Variant, title: string, message?: string) => {
    const id = nextId.current++;
    setToasts(prev => [...prev, { id, variant, title, message }]);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(
        <div className={styles.container}>
          {toasts.map(t => (
            <ToastItem key={t.id} entry={t} onDismiss={dismiss} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx.toast;
}
